
import {defineComponent} from "vue";
import {PostQuery, usePostQuery} from "@/graphql/API";
import {mapActions, mapState} from "pinia";
import {PlayerState, usePlayerStore} from "@/store/player.store";
import {useAnalyticsStore} from "@/store/analytics.store";
import {EmbedType} from "@/providers/analytics.provider";
import {ApolloError, ApolloQueryResult} from "@apollo/client";
import {useUIStore} from "@/store/ui.store";
import BaseView from "@/views/BaseView.vue";
import {Post} from "@/models/post";
import {Profile} from "@/models/profile";
import {Helper} from "@/utils/helper";

interface PostViewData {
    embedType: EmbedType;
    error?: string;
    isShareOverlayVisible: boolean;
    theme?: string;
    appearance?: string;
    showLink: boolean;
}

interface PostURLQuery {
    id?: string;
    theme?: string;
    appearance?: string;
    showLink?: string;
    hideLogo?: string;
}

export const PostNotFoundError = "Post Not Found"

export default defineComponent({
    name: "BasePostView",
    extends: BaseView,
    data(): PostViewData {
        return {
            embedType: EmbedType.Post,
            error: undefined,
            isShareOverlayVisible: false,
            theme: undefined,
            appearance: undefined,
            showLink: true,
        };
    },
    mounted() {
        this.setEmbedType(this.embedType);
        this.trackPageView(this.$route.query);
        this.setCustomTheme();
        this.fetchPost();

        const query: PostURLQuery = this.$route.query;

        if (query.theme) {
            this.theme = query.theme;
        }

        this.appearance = query.appearance;
        this.showLink = query.showLink !== 'false';

        if (query.hideLogo && query.hideLogo === 'true') {
            this.setHideLogo(true);
        }
    },
    methods: {
        // actions from player
        ...mapActions(usePlayerStore, ['setQueue']),
        // tracking actions
        ...mapActions(useAnalyticsStore, ['setEmbedType']),
        ...mapActions(useAnalyticsStore, ['trackPageView']),
        ...mapActions(useAnalyticsStore, ['trackEvent']),
        ...mapActions(useAnalyticsStore, ['trackClickOut']),

        showShareOverlay() {
            this.trackEvent('tappedShareShow', this.$analytics.postParams(this.post))
            this.isShareOverlayVisible = true;
        },
        hideShareOverlay() {
            this.trackEvent('tappedShareHide', this.$analytics.postParams(this.post))
            this.isShareOverlayVisible = false;
        },
        fetchPost() {
            if (!this.getPostID) {
                this.error = "Missing post ID";
                return;
            }

            const { onResult, onError } = usePostQuery({ postID: this.getPostID });

            onResult((result: ApolloQueryResult<PostQuery>) => {
                if (!result || !result.data) {
                    return this.setQueue([]);
                }

                const post = result.data.post as Post;
                if (!post.audio) {
                    console.error("Post without audio.")
                    this.error = PostNotFoundError
                    return this.setQueue([])
                }

                this.trackEmbedViewIfNeeded(post);
                this.setQueue([post]);
            });

            onError((error: ApolloError) => {
                if (!window.Cypress) {
                    Helper.sendMessageToParentWindow({ name: "beamsPostLoadingError" });
                }

                if (error.message.includes("[404]")) {
                  console.error("Post not found.")
                  this.error = PostNotFoundError
                } else {
                  console.error("Error fetching the post: "+error.message)
                  this.error = error.message;
                }
            });
        },

      trackEmbedViewIfNeeded(post: Post) {
          const conversionTrackablePartners: string[] = [
            // "5a45fcba-4f46-43e2-8ab2-27b7bc259fb3" // chamberlaincoffee
          ]

          if (!conversionTrackablePartners.includes(post.profile.id)) {
            return;
          }

          this.trackEvent('embed_view', this.$analytics.postParams(post))
      }
    },
    computed: {
        // state from player
        ...mapState(usePlayerStore, { post: 'getActivePost' }),
        ...mapState(usePlayerStore, ['state' ]),
        // analytics
        ...mapState(useAnalyticsStore, ['portalShareURL']),
        ...mapState(useAnalyticsStore, ["postShareURL"]),
        // ui
        ...mapState(useUIStore, ["getHideLogo"]),

        getPostID(): string | undefined {
            const query: PostURLQuery = this.$route.query;
            if (!query.id) {
                return undefined;
            }

            return query.id;
        },
        profile(): Profile | undefined {
            return this.post ? this.post.profile : undefined;
        },
        showEndBanner(): boolean {
            return this.state === PlayerState.ended;
        },
        getTheme(): object {
            const classObj: {[key: string]: boolean} = {};
            if (this.theme) {
                classObj[this.theme] = true;
            }
            return classObj
        },
    },
});
